import type { TrainingPlan } from '@shared/types';
import { useEffect, useMemo, useState } from 'react';
import { useNotification } from '../contexts/NotificationContext';
import apiClient from '../services/api';
import { logger } from '../utils/logger';
import { getAllWorkouts } from '../utils/planUtils';
import { loadUserProfileSync } from '../utils/userProfile';

/**
 * Hook for managing deload week functionality
 */
export const useDeloadWeek = (
  trainingPlan: TrainingPlan | null,
  currentWeekDates: Date[],
  onTrainingPlanUpdate?: (updatedPlan: TrainingPlan) => void
) => {
  const { showApiError, showSuccess } = useNotification();
  const [currentDeloadWeek, setCurrentDeloadWeek] = useState<number>(4);
  const [isLoadingDeloadWeek, setIsLoadingDeloadWeek] = useState(false);
  const [showDeloadWeekDialog, setShowDeloadWeekDialog] = useState(false);

  // Load current deload week setting
  useEffect(() => {
    const loadDeloadWeek = async () => {
      try {
        const response = await apiClient.getDeloadWeek();
        if (response.success && response.data) {
          setCurrentDeloadWeek(response.data.deloadWeek);
        }
      } catch (error) {
        logger.error('Failed to load deload week', { error });
      }
    };

    loadDeloadWeek();
  }, []);

  // Calculate current week number in training plan
  const getCurrentWeekNumber = (): number | null => {
    const allWorkouts = getAllWorkouts(trainingPlan);
    if (allWorkouts.length === 0 || currentWeekDates.length === 0) return null;

    const firstTrainingDate = new Date(allWorkouts[0].training_date);
    const currentWeekStart = currentWeekDates[0];

    const weekDiff = Math.floor(
      (currentWeekStart.getTime() - firstTrainingDate.getTime()) / (7 * 24 * 60 * 60 * 1000)
    );
    const weekNumber = weekDiff + 2;

    return weekNumber >= 1 && weekNumber <= 5 ? weekNumber : null;
  };

  // Memoized week number calculation
  const currentWeekNumber = useMemo(() => {
    return getCurrentWeekNumber();
  }, [trainingPlan, currentWeekDates]);

  // Memoized deload week check
  const isDeloadWeek = useMemo(() => {
    return currentWeekNumber === currentDeloadWeek;
  }, [currentWeekNumber, currentDeloadWeek]);

  // Handle changing the deload week
  const handleChangeDeloadWeek = async (newWeek: number) => {
    setIsLoadingDeloadWeek(true);
    try {
      const userProfile = loadUserProfileSync();
      if (!userProfile) {
        showApiError(new Error('User profile not found. Please set up your profile first.'));
        return;
      }

      showSuccess(
        `Changing deload week to week ${newWeek}. The entire 5-week training plan will be recalculated based on the new deload timing.`,
        6000
      );

      const response = await apiClient.setDeloadWeek(userProfile, newWeek);
      if (response.success && response.data) {
        setCurrentDeloadWeek(newWeek);
        setShowDeloadWeekDialog(false);

        if (onTrainingPlanUpdate) {
          onTrainingPlanUpdate(response.data);
        }

        showSuccess(
          `✅ Training plan recalculated with deload week ${newWeek}! All workouts have been updated.`
        );
      } else {
        showApiError(new Error(response.message || 'Failed to update deload week'));
      }
    } catch (error) {
      showApiError(error);
    } finally {
      setIsLoadingDeloadWeek(false);
    }
  };

  return {
    currentDeloadWeek,
    currentWeekNumber,
    isDeloadWeek,
    isLoadingDeloadWeek,
    showDeloadWeekDialog,
    setShowDeloadWeekDialog,
    handleChangeDeloadWeek,
  };
};
